import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEmailTemplates, saveEmailTemplate, resetEmailTemplate } from '../../api/portal-api.js';
import {
  NW_EMAIL_TEMPLATE_LABELS,
  NW_TEMPLATE_PLACEHOLDERS,
  findInvalidPlaceholders,
} from '@qdf/shared';
import type { NwEmailTemplate, NwEmailTemplateKind } from '@qdf/shared';

/** Sample values used by the live preview. */
const PREVIEW_VARS: Record<string, string> = {
  first_name: 'Jane',
  full_name: 'Jane Doe',
  entity_name: 'Doe Properties LLC',
  tracking_id: 'AB12CD3',
};

function substitutePreview(text: string): string {
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (whole, name: string) =>
    PREVIEW_VARS[name] !== undefined ? PREVIEW_VARS[name] : whole,
  );
}

const KIND_DESCRIPTIONS: Record<NwEmailTemplateKind, string> = {
  thank_you: 'Sent automatically right after an application is submitted. The tracking-ID box is always appended below this copy.',
  decision_approved: 'Sent from Step 2 (Notify Submitter) when the saved decision is Approve.',
  decision_rejected: 'Sent from Step 2 (Notify Submitter) when the saved decision is Reject.',
  draw_processed: 'Sent from Step 3 (Draw Processed) once the draw for an approved application has been processed.',
};

export function EmailTemplates() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['portal-email-templates'],
    queryFn: fetchEmailTemplates,
  });

  const [editingKind, setEditingKind] = useState<NwEmailTemplateKind | null>(null);
  const [subject, setSubject] = useState('');
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [banner, setBanner] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const showBanner = (msg: string) => {
    setBanner(msg);
    setTimeout(() => setBanner(''), 5000);
  };

  const saveMutation = useMutation({
    mutationFn: (kind: NwEmailTemplateKind) => saveEmailTemplate(kind, { subject, heading, body }),
    onSuccess: (_res, kind) => {
      queryClient.invalidateQueries({ queryKey: ['portal-email-templates'] });
      setEditingKind(null);
      setErrorMsg('');
      showBanner(`Template "${NW_EMAIL_TEMPLATE_LABELS[kind]}" saved — it now applies to all future sends.`);
    },
    onError: (err: any) => {
      setErrorMsg(err?.body?.message || err?.message || 'Save failed');
    },
  });

  const resetMutation = useMutation({
    mutationFn: (kind: NwEmailTemplateKind) => resetEmailTemplate(kind),
    onSuccess: (_res, kind) => {
      queryClient.invalidateQueries({ queryKey: ['portal-email-templates'] });
      setEditingKind(null);
      setErrorMsg('');
      showBanner(`Template "${NW_EMAIL_TEMPLATE_LABELS[kind]}" reset to the built-in default.`);
    },
    onError: (err: any) => {
      setErrorMsg(err?.body?.message || err?.message || 'Reset failed');
    },
  });

  const startEditing = (tpl: NwEmailTemplate) => {
    setEditingKind(tpl.kind);
    setSubject(tpl.subject);
    setHeading(tpl.heading);
    setBody(tpl.body);
    setErrorMsg('');
  };

  const invalidTokens = [
    ...findInvalidPlaceholders(subject),
    ...findInvalidPlaceholders(heading),
    ...findInvalidPlaceholders(body),
  ];

  if (isLoading) {
    return (
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: '#64748b', marginTop: '16px', fontSize: '14px' }}>Loading templates…</p>
      </div>
    );
  }

  const templates = data?.templates ?? [];

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Email Templates</h1>
          <p style={s.subtitle}>
            Copy for every email New Western applicants receive
          </p>
        </div>
      </div>

      <div style={s.scopeBanner}>
        🟨 These templates apply to <strong>New Western</strong> applicant emails only.
        Left Main submissions and communications are not affected in any way.
      </div>

      {banner && <div style={s.successBanner}>✓ {banner}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {templates.map((tpl) => {
          const isEditing = editingKind === tpl.kind;
          return (
            <div key={tpl.kind} style={{ ...s.card, border: isEditing ? '2px solid #c0985c' : '1px solid #e2e8f0' }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <h2 style={s.cardTitle}>✉️ {NW_EMAIL_TEMPLATE_LABELS[tpl.kind]}</h2>
                {tpl.is_customized ? (
                  <span style={{ ...s.badge, background: '#faf6ef', color: '#967448', border: '1px solid #e8dcc8' }}>
                    Customized{tpl.updated_by ? ` · ${tpl.updated_by}` : ''}
                  </span>
                ) : (
                  <span style={{ ...s.badge, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                    Default copy
                  </span>
                )}
                <div style={{ flex: 1 }} />
                {!isEditing && (
                  <button style={s.secondaryBtn} onClick={() => startEditing(tpl)}>✏️ Edit</button>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '6px 0 0', lineHeight: 1.5 }}>
                {KIND_DESCRIPTIONS[tpl.kind]}
              </p>

              {!isEditing ? (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#334155' }}>
                  <div style={{ marginBottom: '4px' }}><strong>Subject:</strong> {tpl.subject}</div>
                  {tpl.heading && <div style={{ marginBottom: '4px' }}><strong>Heading:</strong> {tpl.heading}</div>}
                  <div style={{ whiteSpace: 'pre-wrap', color: '#64748b', borderLeft: '3px solid #e2e8f0', paddingLeft: '10px', marginTop: '6px' }}>
                    {tpl.body}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '18px', marginTop: '14px', flexWrap: 'wrap' }}>
                  {/* Editor */}
                  <div style={{ flex: '1 1 340px', minWidth: '300px' }}>
                    <label style={s.label}>Subject</label>
                    <input style={s.input} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
                    <label style={{ ...s.label, marginTop: '10px' }}>Heading (headline in the email — optional)</label>
                    <input style={s.input} value={heading} onChange={(e) => setHeading(e.target.value)} maxLength={150} />
                    <label style={{ ...s.label, marginTop: '10px' }}>Body (plain text — blank line starts a new paragraph)</label>
                    <textarea
                      style={{ ...s.input, minHeight: '180px', resize: 'vertical' as const, fontFamily: 'inherit', lineHeight: 1.5 }}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      maxLength={5000}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <span style={{ fontSize: '11px', color: '#64748b', marginRight: '6px' }}>Placeholders:</span>
                      {NW_TEMPLATE_PLACEHOLDERS.map((p) => (
                        <button
                          key={p}
                          style={s.chip}
                          title={`Example: ${PREVIEW_VARS[p]}`}
                          onClick={() => setBody((prev) => `${prev}{{${p}}}`)}
                        >
                          {`{{${p}}}`}
                        </button>
                      ))}
                    </div>
                    {invalidTokens.length > 0 && (
                      <div style={s.errorBox}>
                        Unknown placeholder(s): {invalidTokens.map((t) => `{{${t}}}`).join(', ')}
                      </div>
                    )}
                    {errorMsg && <div style={s.errorBox}>⚠️ {errorMsg}</div>}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                      <button
                        style={{ ...s.primaryBtn, opacity: saveMutation.isPending || invalidTokens.length > 0 || !subject.trim() || !body.trim() ? 0.5 : 1 }}
                        disabled={saveMutation.isPending || invalidTokens.length > 0 || !subject.trim() || !body.trim()}
                        onClick={() => saveMutation.mutate(tpl.kind)}
                      >
                        {saveMutation.isPending ? '⏳ Saving…' : '💾 Save Template'}
                      </button>
                      {tpl.is_customized && (
                        <button
                          style={s.dangerBtn}
                          disabled={resetMutation.isPending}
                          onClick={() => resetMutation.mutate(tpl.kind)}
                        >
                          {resetMutation.isPending ? '⏳' : '↩️ Reset to Default'}
                        </button>
                      )}
                      <button style={s.secondaryBtn} onClick={() => { setEditingKind(null); setErrorMsg(''); }}>Cancel</button>
                    </div>
                  </div>

                  {/* Live preview */}
                  <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
                    <label style={s.label}>Preview (sample applicant data)</label>
                    <div style={s.previewShell}>
                      <div style={s.previewHeader}>New Western</div>
                      <div style={{ fontSize: '11px', color: '#64748b', margin: '10px 0 4px' }}>
                        <strong>Subject:</strong> {substitutePreview(subject)}
                      </div>
                      {heading.trim() && (
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#14213d', margin: '10px 0' }}>
                          {substitutePreview(heading)}
                        </div>
                      )}
                      {substitutePreview(body).split(/\n\s*\n/).map((para, i) => (
                        <p key={i} style={{ fontSize: '12px', color: '#333', lineHeight: 1.5, margin: '8px 0', whiteSpace: 'pre-wrap' }}>
                          {para.trim()}
                        </p>
                      ))}
                      {tpl.kind === 'thank_you' && (
                        <div style={{ background: '#f5f5f5', borderLeft: '4px solid #fca311', padding: '10px 14px', margin: '12px 0' }}>
                          <div style={{ fontSize: '10px', color: '#666' }}>Your tracking ID</div>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: '#14213d', letterSpacing: '1px' }}>AB12CD3</div>
                        </div>
                      )}
                      <div style={{ borderTop: '1px solid #ddd', marginTop: '14px', paddingTop: '8px', fontSize: '9px', color: '#999' }}>
                        This is an automated message from New Western. Please do not reply to this email.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: '28px 32px', maxWidth: '1100px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' },
  title: { fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', margin: '4px 0 0' },
  scopeBanner: {
    background: '#faf6ef', border: '1px solid #e8dcc8', color: '#7c5f3b',
    padding: '10px 14px', borderRadius: '10px', fontSize: '12px', marginBottom: '16px', lineHeight: 1.5,
  },
  successBanner: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#047857',
    padding: '10px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '16px',
  },
  card: {
    background: '#fff', borderRadius: '14px', padding: '18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTitle: { fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 },
  badge: { padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600 },
  label: { display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px',
    fontSize: '13px', boxSizing: 'border-box' as const,
  },
  chip: {
    display: 'inline-block', margin: '2px 4px 2px 0', padding: '2px 8px',
    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px',
    fontSize: '11px', fontFamily: 'monospace', color: '#334155', cursor: 'pointer',
  },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
    padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginTop: '8px',
  },
  primaryBtn: {
    background: '#0f766e', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  },
  secondaryBtn: {
    background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
  },
  dangerBtn: {
    background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
  },
  previewShell: {
    background: '#fff', border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '14px',
  },
  previewHeader: {
    background: '#14213d', color: '#fff', fontWeight: 700, fontSize: '13px',
    padding: '10px 14px', borderRadius: '6px', letterSpacing: '0.5px',
  },
  loadingWrap: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: '80px 0' },
  spinner: {
    width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0f766e',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
};
